import type { CwyTransferRequest } from '../services/walletService';
import { formatTransactionTimestamp } from '../../../utils/formatTransactionTimestamp';

export const TRANSFER_PROGRESS_STEPS = [
  { key: 'submitted', label: 'Submitted', description: 'Request received' },
  { key: 'review', label: 'Admin review', description: 'Waiting for approval' },
  { key: 'transfer', label: 'Vault transfer', description: 'Sending from treasury' },
  { key: 'complete', label: 'In your wallet', description: 'Tokens credited' },
] as const;

export type TransferProgressVariant = 'active' | 'complete' | 'failed';

export interface TransferProgressState {
  stepIndex: number;
  percent: number;
  variant: TransferProgressVariant;
  headline: string;
  subline: string;
  failedStepIndex?: number;
  indeterminate: boolean;
}

export function isGrantRequest(request: CwyTransferRequest): boolean {
  return (request.request_type ?? 'phase1_test_grant') === 'phase1_test_grant';
}

export function isActiveGrantRequest(request: CwyTransferRequest): boolean {
  return isGrantRequest(request) && (request.status === 'pending' || request.status === 'processing');
}

export function getTransferProgressState(request: CwyTransferRequest): TransferProgressState {
  const amount = formatGrantAmount(request.amount_cwy);

  switch (request.status) {
    case 'pending':
      return {
        stepIndex: 1,
        percent: 50,
        variant: 'active',
        headline: `${amount} CWY awaiting approval`,
        subline: 'An admin will review your request shortly.',
        indeterminate: false,
      };
    case 'processing':
      return {
        stepIndex: 2,
        percent: 75,
        variant: 'active',
        headline: `Transferring ${amount} CWY from vault`,
        subline: request.reviewed_by_name
          ? `Approved by ${request.reviewed_by_name} — confirming on Solana…`
          : 'Building and confirming the on-chain transfer…',
        indeterminate: true,
      };
    case 'completed':
      return {
        stepIndex: 3,
        percent: 100,
        variant: 'complete',
        headline: `${amount} CWY received`,
        subline: 'Tokens are in your linked wallet.',
        indeterminate: false,
      };
    case 'rejected':
      return {
        stepIndex: 1,
        percent: 50,
        variant: 'failed',
        headline: 'Request declined',
        subline: request.rejection_reason || 'Your request was not approved.',
        failedStepIndex: 1,
        indeterminate: false,
      };
    case 'failed':
      return {
        stepIndex: 2,
        percent: 75,
        variant: 'failed',
        headline: 'Transfer failed',
        subline: request.tx_error || 'The on-chain transfer could not be completed.',
        failedStepIndex: 2,
        indeterminate: false,
      };
    default:
      return {
        stepIndex: 0,
        percent: 25,
        variant: 'active',
        headline: 'Processing request',
        subline: '',
        indeterminate: false,
      };
  }
}

function formatGrantAmount(amount: number): string {
  const trimmed = amount.toFixed(9).replace(/\.?0+$/, '');
  return trimmed || '0';
}

export function formatProgressDateTime(value?: string, timeZone?: string): string {
  return formatTransactionTimestamp(value, timeZone);
}
